  // src/Home/home.js
import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { statisticService, systemService, adminService, enterpriseService } from '../services';
import { useAuth } from '../context/AuthContext';
import { isCapacitor, isElectron } from '../utils/platform';
import './home.css';
import '../Settings/Settings.css';
import { createLogger } from '../services/Debug';
import Statistics from './Statistics';
import StatisticsMobile from './StatisticsMobile';
import { Statistic } from '../models/Statistic';
import DailyCard from './DailyCard';

// Components
import Card from '../components/Card/Card';
import Button from '../components/buttons/Button';
import { formatDisplayName } from '../models/ReportTypeDesignHelper';
import UpdateModal from '../components/UpdateModal';
import SectionHeader from '../components/SectionHeader/SectionHeader';
import SkeletonManager from '../components/loaders/SkeletonManager';
import RecorderSidePanel from '../components/RecorderSidePanel/RecorderSidePanel';
import { useRecorderPanel } from '../context/RecorderPanelContext';

// Icons
import { 
  FaFileImport,
  FaFileWord,
  FaCogs,
  FaQuestionCircle,
  
  FaFileContract,
  FaShieldAlt,
  FaSignOutAlt,
  
  FaBuilding,
  FaLightbulb,
  
  FaMicrophone,
  FaKeyboard,
  FaRocket,
  FaStar,
  FaClipboardList,
  FaMobileAlt,
  FaUserShield,
  FaBook,
  FaCog,
  FaInfoCircle,
  FaKeyboard as FaCharacters,
  FaPersons,
  FaPowerOff,
  FaBars,
  FaCheckCircle,
  FaPlus,
  FaCalendarAlt,
  FaChevronRight
} from 'react-icons/fa';
import { SPECIALTIES } from '../constants/specialties';

// Tip packages based on user tenure
const tipPackages = {
  // Package 1: < 1 week (New users)
  beginner: [
    {
      icon: FaMicrophone,
      title: "Commencez votre première dictée",
      text: "Appuyez sur le microphone pour commencer. Parlez naturellement, nous nous occupons du reste!"
    },
    {
      icon: FaKeyboard,
      title: "Raccourci clavier pratique",
      text: "Configurez un raccourci clavier pour démarrer/arrêter l'enregistrement rapidement."
    },
    {
      icon: FaLightbulb,
      title: "Modèles",
      text: "N'oubliez pas de sélectionner le modèle avant d'envoyer pour un meilleur formatage."
    }
  ],
  // Package 2: < 1 month (Getting comfortable)
  intermediate: [
    {
      icon: FaStar,
      title: "Favoris intelligents",
      text: "Marquez vos types de rapports fréquents en favoris pour un accès plus rapide."
    },
    {
      icon: FaLightbulb,
      title: "Annexes multiples",
      text: "Vous pouvez ajouter plusieurs photos ou documents en annexe d'un seul rapport."
    }
  ],
  // Package 3: < 3 months (Regular users)
  advanced: [
    {
      icon: FaLightbulb,
      title: "Instructions personnalisées",
      text: "Définissez des instructions spécifiques par template pour des résultats optimaux."
    }
  ],
  // Package 4: > 3 months (Expert users)
  expert: [
    {
      icon: FaStar,
      title: "Optimisez votre workflow",
      text: "Combinez macros, favoris et instructions personnalisées pour un gain de temps maximal."
    },
    {
      icon: FaLightbulb,
      title: "Traduction instantanée",
      text: "Utilisez le traducteur médical intégré pour communiquer avec des patients internationaux."
    }
  ]
};

function Home({ handleLogout }) {
  const log = createLogger('Home');
  const { profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const { isOpen: isRecorderPanelOpen, isWebOnly } = useRecorderPanel();
  
  // Initial loading state
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  
  const [currentTip, setCurrentTip] = useState(null);
  const [showTip, setShowTip] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [period, setPeriod] = useState('global'); // 'global' or 'month'
  const [updateInfo, setUpdateInfo] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [hasSystemMessage, setHasSystemMessage] = useState(false);
  const [systemMessage, setSystemMessage] = useState(null);
  const [remoteTipPackages, setRemoteTipPackages] = useState(null);
  const [tipPackageOverride, setTipPackageOverride] = useState(null);
  // Create Organization modal state
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [createOrgError, setCreateOrgError] = useState(null);
  const [createOrgForm, setCreateOrgForm] = useState({
    name: '',
    shortName: '',
    specialty: '',
    description: ''
  });
  const [showCreateOrgSuccess, setShowCreateOrgSuccess] = useState(false);
  const [createdEnterprise, setCreatedEnterprise] = useState(null);
  // Leave enterprise modal state (non-admin)
  const [showLeaveEnterprise, setShowLeaveEnterprise] = useState(false);
  const [enterpriseToLeave, setEnterpriseToLeave] = useState(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [leavingEnterprise, setLeavingEnterprise] = useState(false);

  useEffect(() => {
    setIsInitialLoading(true);
    loadUserData();
  }, []);

  // Refetch when period changes
  useEffect(() => {
    fetchStatistics(period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  // Load user data (same pattern as Settings.js)
  const loadUserData = async () => {
    const minimumLoadingTime = 500;
    const startTime = Date.now();
    
    try {
      await refreshProfile();
      
      if (!profile) {
        throw new Error('Profile data missing');
      }
      
      initDailyTip(profile);
      
      // Load other data in parallel
      await Promise.all([
        fetchStatistics('global'),
        checkForUpdates(),
        fetchSystemMessage()
      ]);
      
      // Ensure minimum loading time for smooth UX
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
      
    } catch (error) {
      log.error('Error loading user data:', error);
      
    
      
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
    } finally {
      // Hide global loading overlay
      setIsInitialLoading(false);
    }
  };

  const openCreateOrganization = () => {
    setCreateOrgError(null);
    setCreateOrgForm({
      name: '',
      shortName: '',
      specialty: profile?.user?.specialty || 'cardiologie',
      description: ''
    });
    setShowCreateOrg(true);
  };

  // Preview helper for shortName formatting (free text input, formatted on send)
  const previewShortName = (value) => {
    if (value.length <= 2) return '';
    return String('consultation ' + formatDisplayName(value || '', true))
    .slice(0, 32);
  }
  // Sanitize to allow only letters (incl. accents) and spaces
  const sanitizeLettersAndSpaces = (value) => String(value || '').replace(/[^A-Za-zÀ-ÖØ-öø-ÿ\s]/g, '');

  // Final normalization for submission: lowercase, trim, spaces->underscore, remove accents and specials, cap length
  const normalizeShortNameForSubmit = (value) => {
    let v = String(value || '');
    // Remove diacritics
    v = v.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    v = v.toLowerCase();
    v = v.trim();
    // Only keep letters (a-z) and spaces
    v = v.replace(/[^a-z\s]/g, '');
    // Enforce reasonable max length
    return v.slice(0, 20);
  };

  const handleCreateOrgChange = (field, value) => {
    setCreateOrgError(null);
    if (field === 'shortName') {
      value = sanitizeLettersAndSpaces(value);
    }
    setCreateOrgForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitCreateOrg = async () => {
    const { name, shortName, specialty, description } = createOrgForm;
    if (!name || name.trim().length < 5) {
      setCreateOrgError("Nom obligatoire (minimum 5 caractères).");
      return;
    }
    // Second verification and strict normalization at submission time
    const safeShortName = normalizeShortNameForSubmit(shortName);
    if (!safeShortName || safeShortName.length < 5) {
      setCreateOrgError("Identifiant court invalide. Minimum 5 caractères, lettres uniquement.");
      return;
    }
    const payload = {
      name: name.trim(),
      shortName: safeShortName,
      specialty: (specialty || '').trim() || 'cardiologie',
      description: (description || '').trim()
    };
    try {
      setCreatingOrg(true);
      const created = await adminService.createEnterprise(payload);
      setShowCreateOrg(false);
      setCreateOrgForm({ name: '', shortName: '', specialty: '', description: '' });
      setCreatedEnterprise(created || payload);
      setShowCreateOrgSuccess(true);
      // Refresh profile to include the new enterprise
      await loadUserData();
    } catch (error) {
      log.error('Error creating enterprise:', error);
      setCreateOrgError("Cet identifiant est déjà utilisé. Veuillez en choisir un autre.");
    } finally {
      setCreatingOrg(false);
    }
  };

  // Open leave panel for non-admin users
  const openLeaveEnterprisePanel = (enterprise) => {
    setEnterpriseToLeave(enterprise || null);
    setConfirmLeave(false);
    setShowLeaveEnterprise(true);
  };

  const closeLeaveEnterprisePanel = () => {
    if (leavingEnterprise) return;
    setShowLeaveEnterprise(false);
    setEnterpriseToLeave(null);
    setConfirmLeave(false);
  };

  const handleLeaveEnterprise = async () => {
    if (!enterpriseToLeave?.id) return;
    setLeavingEnterprise(true);
    try {
      await enterpriseService.leaveEnterprise(enterpriseToLeave.id);
      setShowLeaveEnterprise(false);
      setEnterpriseToLeave(null);
      setConfirmLeave(false);
      // Refresh profile to reflect removal
      await loadUserData();
    } catch (err) {
      // Optionally handle error UI
    } finally {
      setLeavingEnterprise(false);
    }
  };

  // Calculate user tenure and select appropriate tip package
  const getUserTenurePackage = (profile) => {
    if (!profile?.user?.createdAt) return 'beginner';
    
    const createdDate = new Date(profile.user.createdAt);
    const now = new Date();
    const daysSinceCreation = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
    
    if (daysSinceCreation < 7) return 'beginner';
    if (daysSinceCreation < 30) return 'intermediate';
    if (daysSinceCreation < 90) return 'advanced';
    return 'expert';
  };

  // Fetch system message and store text only (legacy kept inline in new fetch below)

  // Daily tip helpers
  const getTodayKey = () => new Date().toISOString().slice(0, 10);

  // Initialize daily tip (persist per day, respect hide)
  const initDailyTip = (profile) => {
    // Prefer remote tips; fallback to local tipPackages
    const effectivePackages = remoteTipPackages || tipPackages;
    const chosenPackage = tipPackageOverride || getUserTenurePackage(profile);
    const tips = effectivePackages[chosenPackage];
    if (!tips || tips.length === 0) return;

    const today = getTodayKey();
    const hiddenDate = localStorage.getItem('dailyTipHiddenDate');
    if (hiddenDate === today) {
      setShowTip(false);
      return;
    }

    const storedPackage = localStorage.getItem('dailyTipPackage');
    if (storedPackage !== chosenPackage) {
      // Reset rotation when package changes
      localStorage.setItem('dailyTipPackage', chosenPackage);
      localStorage.removeItem('dailyTipDate');
      localStorage.removeItem(`dailyTipIndex_${chosenPackage}`);
    }

    const lastDate = localStorage.getItem('dailyTipDate');
    const indexKey = `dailyTipIndex_${chosenPackage}`;
    let index = parseInt(localStorage.getItem(indexKey) || '-1');

    if (lastDate !== today) {
      index = (index + 1) % tips.length;
      localStorage.setItem(indexKey, index.toString());
      localStorage.setItem('dailyTipDate', today);
    } else if (index < 0) {
      index = 0;
      localStorage.setItem(indexKey, index.toString());
    }

    setCurrentTip({ ...tips[index], packageKey: chosenPackage, index });
    setShowTip(true);
  };

  const handleNextTip = () => {
    if (!profile) return;
    const effectivePackages = remoteTipPackages || tipPackages;
    const chosenPackage = tipPackageOverride || getUserTenurePackage(profile);
    const tips = effectivePackages[chosenPackage];
    if (!tips || tips.length === 0) return;
    const indexKey = `dailyTipIndex_${chosenPackage}`;
    let index = parseInt(localStorage.getItem(indexKey) || '0');
    index = (index + 1) % tips.length;
    localStorage.setItem(indexKey, index.toString());
    setCurrentTip({ ...tips[index], packageKey: chosenPackage, index });
  };

  const handleHideTipToday = () => {
    localStorage.setItem('dailyTipHiddenDate', getTodayKey());
    setShowTip(false);
  };

  // Fetch user statistics
  const fetchStatistics = async (selectedPeriod) => {
    try {
      const rawStats = await statisticService.getUserStatistics(selectedPeriod);
      const statsModel = new Statistic({ statistics: rawStats.statistics || rawStats, profile: profile });
      setStatistics(statsModel);
    } catch (error) {
      log.error('Error loading statistics:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  // Check for app updates (Electron only)
  const checkForUpdates = async () => {
    // Only check for updates in Electron
    if (!isElectron) return;

    try {
      // Get current app version
      const currentVersion = await window.electronAPI?.getAppVersion?.();
      if (!currentVersion) return;

      // Check for updates
      const versionData = await systemService.checkVersion('electron', currentVersion);
      
      if (versionData.needsUpdate) {
        setUpdateInfo(versionData);
        setShowUpdateModal(true);
        
        // Store update check timestamp
        localStorage.setItem('lastUpdateCheck', new Date().toISOString());
      }
    } catch (error) {
      log.error('Error checking for updates:', error);
    }
  };

  // Map API icon names to local Fa icons
  const mapIconNameToComponent = (name) => {
    const normalized = String(name || '').toLowerCase();
    const map = {
      microphone: FaMicrophone,
      mic: FaMicrophone,
      keyboard: FaKeyboard,
      lightbulb: FaLightbulb,
      bulb: FaLightbulb,
      star: FaStar,
      rocket: FaRocket
    };
    return map[normalized] || FaLightbulb;
  };

  // Format member since date
  const getMemberSinceText = () => {
    if (!profile?.user?.createdAt) return '';
    
    const createdDate = new Date(profile.user.createdAt);
    const options = { year: 'numeric', month: 'long' };
    return createdDate.toLocaleDateString('fr-FR', options);
  };

  // Fetch system message and remote tips
  const fetchSystemMessage = async () => {
    try {
      const platform = systemService.constructor.detectPlatform();
      const messageData = await systemService.getSystemMessage(platform);

      // Support legacy and both new shapes: envelope or already-unwrapped data
      let messageText = null;
      let astuces = null;
      let recommendedLevel = null;
      if (typeof messageData?.message === 'string') {
        // legacy: { message: '...' }
        messageText = messageData.message;
      } else if (messageData?.data) {
        // new envelope shape: { success, data: { message: { message }, astuces, recommendedLevel } }
        messageText = messageData.data?.message?.message || null;
        astuces = messageData.data?.astuces || null;
        recommendedLevel = messageData.data?.recommendedLevel || null;
      } else if (messageData?.message && typeof messageData.message === 'object') {
        // unwrapped inner data shape: { message: { message }, astuces, recommendedLevel }
        messageText = messageData.message?.message || null;
        astuces = messageData.astuces || null;
        recommendedLevel = messageData.recommendedLevel || null;
      }

      setSystemMessage(messageText || null);
      setHasSystemMessage(!!messageText);

      if (astuces && typeof astuces === 'object') {
        const mapped = {};
        for (const level of ['beginner', 'intermediate', 'advanced', 'expert']) {
          const items = Array.isArray(astuces[level]) ? astuces[level] : [];
          mapped[level] = items.map((tip) => ({
            icon: mapIconNameToComponent(tip.icon),
            title: tip.title,
            text: tip.text
          }));
        }
        setRemoteTipPackages(mapped);

        // If a recommended level comes from backend, reset rotation into that package
        if (recommendedLevel && mapped[recommendedLevel] && mapped[recommendedLevel].length > 0) {
          localStorage.setItem('dailyTipPackage', recommendedLevel);
          localStorage.removeItem('dailyTipDate');
          localStorage.removeItem(`dailyTipIndex_${recommendedLevel}`);
          setTipPackageOverride(recommendedLevel);
        }
      }
    } catch (error) {
      log.error('Error fetching system message:', error);
      setSystemMessage(null);
    }
  };

  // When remote tips arrive, (re)initialize daily tip
  useEffect(() => {
    if (remoteTipPackages && profile) {
      initDailyTip(profile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteTipPackages]);

  // Reinitialize when backend recommends a different package
  useEffect(() => {
    if (tipPackageOverride && profile) {
      initDailyTip(profile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipPackageOverride]);




  // Check if profile is loaded - but don't show error during initial loading
  if (!profile && !isInitialLoading) {
    return (
      <div className="home-container">
        <div className="error-container">
          <div className="error-message">
            Impossible de charger les données du profil. 
            <button onClick={loadUserData} className="retry-button">
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Early return if still loading or no profile
  if (isInitialLoading || !profile) {
    return (
        <>
          <SkeletonManager active={true} />
        </>
    );
  }

  const memberSinceText = getMemberSinceText();

  const panelOpenClass = isWebOnly && isRecorderPanelOpen ? 'recorder-panel-open' : '';

  return (
    <>
    <div className={`home-container ${panelOpenClass}`}>
      {/* Update Modal for Electron */}
      {showUpdateModal && updateInfo && (
        <UpdateModal
          updateInfo={updateInfo}
          onClose={() => setShowUpdateModal(false)}
        />
      )}

  
      <DailyCard
  currentTip={currentTip && showTip ? currentTip : null}
  userProfile={profile}
  memberSinceText={memberSinceText}
  onHideTip={handleHideTipToday}
  onNextTip={handleNextTip}
  systemMessage={systemMessage}
  isCapacitor={isCapacitor}
/>


      {/* Quick Actions */}
      <Card>
        <SectionHeader icon={FaBars} title="Menu" />
        <div className="home-links">
          {/* Show settings for all users (web and mobile) */}
          <NavLink to="/settings" className="home-link-button">
            <FaCogs />
            <span>Réglages</span>
          </NavLink>

          <NavLink to="/report-types/" className="home-link-button">
            <FaClipboardList />
            <span> Gestion des modèles de rapports</span>
          </NavLink>

          {/* Hide Templates Word on Capacitor with Silver plan */}
          {!(isCapacitor && profile?.subscription?.planType?.toLowerCase() === 'silver') && (
            <NavLink to="/templates" className="home-link-button">
              <FaFileWord />
              <span>Gestion des templates Word</span>
            </NavLink>
          )}

          {/*<NavLink to="/translator" className="home-link-button">
            <FaLanguage />
            <span>Traducteur médical</span>
          </NavLink>*/}

          {/* Electron-only features */}
          {isElectron && (
            <>
              <NavLink to="/macros" className="home-link-button">
                <FaKeyboard />
                <span>Gestion des macros</span>
              </NavLink>

              <NavLink to="/dictaphone" className="home-link-button">
                <FaMicrophone />
                <span>Dictaphone physique</span>
              </NavLink>
            </>
          )}
        </div>
      </Card>


{/* Enterprise Section (if applicable) */}
{((profile?.enterprises && Object.keys(profile.enterprises).length > 0) || profile?.isGold) && (
  <Card>
    <SectionHeader icon={FaBuilding} title="Vos organisations" variant="success" titleGradient={true} />
   

    <div className="enterprises-list">
      <div className="enterprises-grid">
        {Object.values(profile.enterprises || {}).map((enterprise) => (
          <div
            key={enterprise.id}
            className={`enterprise-card${enterprise.isActive ? ' active' : ''}`}
            onClick={enterprise.isAdmin ? () => navigate('/enterprise') : undefined}
            style={{ cursor: enterprise.isAdmin ? 'pointer' : 'default' }}
          >
            <div className="enterprise-header">
              <div className="enterprise-avatar">
                <span>{String(enterprise.name || '?').charAt(0).toUpperCase()}</span>
              </div>
              <div className="enterprise-info">
                <h5>{enterprise.name}</h5>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  {enterprise.shortName ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <code style={{ background: '#f1f3f5', color: '#495057', padding: '2px 6px', borderRadius: 6, fontSize: 12 }}>
                        {enterprise.shortName}
                      </code>
                    </span>
                  ) : null}
                  {enterprise.joinedAt ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#495057', fontSize: 13, fontWeight: 600 }}>
                      <FaCalendarAlt />
                      <span>
                        Membre depuis {new Date(enterprise.joinedAt).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' })}
                      </span>
                    </span>
                  ) : null}
                </div>
                {enterprise.description && !isCapacitor && (
                  <p style={{ marginTop: 8, marginBottom: 0 }}>{enterprise.description}</p>
                )}
              </div>
              <div style={{ marginLeft: 'auto', alignSelf: 'stretch', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {enterprise.isActive && (
                    <span className="active-badge">Active</span>
                  )}
                  {enterprise.isAdmin && (
                    <span className="admin-badge"><FaShieldAlt size={12} /> Admin</span>
                  )}
                </div>
                {enterprise.isAdmin ? (
                  <div style={{ color: '#ced4da', marginTop: 'auto' }}>
                    <FaChevronRight />
                  </div>
                ) : (
                  <div style={{ marginTop: 'auto' }}>
                    <Button 
                      variant="danger" 
                      onClick={(e) => { e.stopPropagation(); openLeaveEnterprisePanel(enterprise); }}
                      
                      style={{ padding: '4px 8px', fontSize: 12 }}
                    >
                      Quitter
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
    {profile?.isGold && (() => {
      const enterpriseCount = Array.isArray(profile?.enterprises)
        ? profile.enterprises.length
        : Object.values(profile?.enterprises || {}).length;
      const isCreateDisabled = enterpriseCount >= 5;
      return (
        <div className="home-links success" style={{ marginTop: 8 }}>
          <NavLink
            className="home-link-button"
            onClick={openCreateOrganization}
            disabled={isCreateDisabled}
            title={isCreateDisabled ? 'Limite atteinte: maximum 5 organisations' : ''}
            style={isCreateDisabled ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
          >
            <FaPlus />
            <span>Créer une organisation</span>
          </NavLink>
        </div>
      );
    })()}
  </Card>
)}


      {/* Statistics Section */}
      {isCapacitor ? (
        <StatisticsMobile
          statistics={statistics}
          period={period}
          onPeriodChange={setPeriod}
          loading={statsLoading}
        />
      ) : (
        <Statistics 
          statistics={statistics}
          period={period}
          onPeriodChange={setPeriod}
          loading={statsLoading}
        />
      )}

      {/* Guide & FAQ Section */}
      <Card>
        <SectionHeader icon={FaBook} title="Ressources" variant="info" />
        <div className="home-links info">
          <NavLink to="/guide" className="home-link-button"><FaQuestionCircle /> Guide d'utilisation</NavLink>
          <NavLink to="/faq" className="home-link-button"><FaLightbulb /> FAQ</NavLink>
          {!isElectron && !isCapacitor && (
            <NavLink to="/download" className="home-link-button"><FaMobileAlt /> Télécharger l'application bureau</NavLink>
          )}
          <NavLink to="/contact" className="home-link-button"><FaCog /> Support technique</NavLink>
        </div>
      </Card>

      {/* Administration Section */}
      {(profile?.isAdmin && (
        <Card>
          <SectionHeader icon={FaUserShield} title="Administration" variant="warning" />
          <div className="home-links">
            
            {/* System Admin */}
            {profile?.isAdmin && (
              <NavLink to="/admin" className="home-link-button">
                <FaUserShield />
                <span>Admin système</span>
              </NavLink>
            )}
          </div>
        </Card>
      ))}

      {/* Information & Support */}
      <Card>
        <SectionHeader icon={FaShieldAlt} title="Informations" variant="secondary" />
        <div className="home-links secondary">
        <NavLink to="/about" className="home-link-button"><FaInfoCircle /> À propos</NavLink>
          <NavLink to="/privacy" className="home-link-button">
            <FaShieldAlt />
            <span>Politique de confidentialité</span>
          </NavLink>

          <NavLink to="/terms" className="home-link-button">
            <FaFileContract />
            <span>Conditions d'utilisation</span>
          </NavLink>
        </div>
      </Card>

      {/* Logout Section */}
      <Card>
        <div className="home-section-logout">
          <Button 
            variant="danger" 
            onClick={handleLogout}
            icon={FaSignOutAlt}
            collapseOnSmallScreen={false}
          >
            Se déconnecter
          </Button>
        </div>
      </Card>

    </div>
    {showCreateOrg && (
      <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowCreateOrg(false); }}>
        <div className="modal" style={{ maxWidth: 720 }} onMouseDown={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Créer une organisation</h3>
          </div>
          <div className="modal-body">
            <p style={{ marginTop: 0 }}>
              Les organisations sont des espaces d'équipe pour collaborer sur vos dictées, modèles et tempates Word.
              Donnez un nom, un identifiant court et choisissez une spécialité. Vous pourrez inviter des collègues ensuite.
            </p>

            <div className="form-group">
              <label>Nom*</label>
              <input
                type="text"
                value={createOrgForm.name}
                onChange={(e) => handleCreateOrgChange('name', e.target.value)}
                placeholder={`ex: ABC service de ${profile.specialty}`}
              />
              <small style={{ color: '#6c757d' }}>Min. 5 caractères</small>
            </div>

            <div className="form-group" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'end' }}>
              <div>
                <label>Identifiant court</label>
                <input
                  type="text"
                  value={createOrgForm.shortName}
                  onChange={(e) => handleCreateOrgChange('shortName', e.target.value)}
                  placeholder={`ex: ABC ${profile.specialty.slice(0, 6)}`}
                />
                <small style={{ color: '#6c757d' }}>Lettres et espaces uniquement, min. 5 et max. 20 caractères</small>
              </div>
              <div>
                <label>Aperçu des modèles de l'organisation</label>
                <div style={{
                  border: '1px solid #e9ecef',
                  borderRadius: 8,
                  padding: '10px 12px',
                  background: '#f8f9fa',
                  minHeight: 38,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <strong>{previewShortName(createOrgForm.shortName) || ''}</strong>
                </div>
                {/* Reserve the same helper-text space to align with the left column */}
                <small style={{ visibility: 'hidden' }}>Lettres et espaces uniquement</small>
              </div>
            </div>

            <div className="form-group">
              <label>Spécialité</label>
              <select
                value={createOrgForm.specialty || ''}
                onChange={(e) => handleCreateOrgChange('specialty', e.target.value)}
              >
                {(SPECIALTIES || []).map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Description (optionnel)</label>
              <textarea
                rows={3}
                value={createOrgForm.description}
                onChange={(e) => handleCreateOrgChange('description', e.target.value)}
                placeholder="Espace d'équipe"
                style={{ fontFamily: 'Arial, sans-serif' }}
              />
            </div>

            {createOrgError && (
              <div style={{ color: 'var(--color-danger)', marginTop: 8 }}>{createOrgError}</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <Button  variant="primary" 
            onClick={() => setShowCreateOrg(false)} disabled={creatingOrg}
            type="inversed"
            >
              Annuler
            </Button>
            <Button  variant="primary" 
            onClick={handleSubmitCreateOrg} disabled={
              creatingOrg ||
              normalizeShortNameForSubmit(createOrgForm.shortName).length < 5 ||
              (createOrgForm.name || '').trim().length < 5
            }
            type="fixed"
            icon={FaBuilding}
            >
              {creatingOrg ? 'Création…' : 'Créer'}
            </Button>
          </div>
        </div>
      </div>
    )}

    {showCreateOrgSuccess && (
      <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowCreateOrgSuccess(false); }}>
        <div
          className="modal"
          style={{
            maxWidth: 720,
            overflow: 'hidden',
            animation: 'successPop 260ms ease-out',
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <FaCheckCircle style={{ color: 'var(--color-success)', fontSize: 28 }} />
            <h3 style={{ margin: 0 }}>Organisation créée avec succès</h3>
          </div>
          <p style={{ marginTop: 0, color: '#546E7A' }}>
            {createdEnterprise?.name ? `« ${createdEnterprise.name} »` : 'Votre organisation'} est prête. Voici quelques actions rapides pour commencer :
          </p>
          <div className="home-links" style={{ marginTop: 8 }}>
            <NavLink to="/enterprise" className="home-link-button">
              <FaUserShield /> Gérer les utilisateurs
            </NavLink>
            <NavLink to="/report-types/" className="home-link-button">
              <FaClipboardList /> Créer un modèle de rapport
            </NavLink>
            <NavLink to="/enterprise" className="home-link-button">
              <FaBuilding /> Importer un logo
            </NavLink>
            <NavLink to="/templates" className="home-link-button">
              <FaFileImport /> Importer un template Word
            </NavLink>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
            <Button
              variant="secondary"
              onClick={() => setShowCreateOrgSuccess(false)}
              type="inversed"
            >
              Plus tard
            </Button>
            <Button
              variant="primary"
              onClick={() => { setShowCreateOrgSuccess(false); navigate('/enterprise'); }}
              icon={FaBuilding}
              type="fixed"
            >
              OK
            </Button>
          </div>
        </div>
        {/* Local animation keyframes */}
        <style>{`@keyframes successPop { from { opacity: 0; transform: translateY(8px) scale(.98); } to { opacity: 1; transform: translateY(0) scale(1); } }`}</style>
      </div>
    )}

    {showLeaveEnterprise && enterpriseToLeave && (
      <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) closeLeaveEnterprisePanel(); }}>
        <div className="modal" style={{ maxWidth: 560 }} onMouseDown={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Quitter l'organisation</h3>
          </div>
          <div className="modal-body">
            <p style={{ marginTop: 0 }}>Vous êtes sur le point de quitter l'organisation « {enterpriseToLeave.name} ».</p>
            <p style={{ color: '#6c757d' }}>Cette action supprimera votre accès aux modèles, rapports et paramètres de cette organisation.</p>
            <p style={{ color: '#6c757d' }}>Si cette organisation vous offre l'abonnement, il sera annulé.</p>
            <div style={{ marginTop: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={confirmLeave} onChange={(e) => setConfirmLeave(e.target.checked)} />
                <span>Je comprends et je confirme quitter « {enterpriseToLeave.name} »</span>
              </label>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={closeLeaveEnterprisePanel} type="inversed" disabled={leavingEnterprise}>
              Annuler
            </Button>
            <Button variant="danger" onClick={handleLeaveEnterprise} disabled={!confirmLeave || leavingEnterprise} type="fixed" icon={FaSignOutAlt}>
              {leavingEnterprise ? 'Départ...' : 'Quitter l\'organisation'}
            </Button>
          </div>
        </div>
      </div>
    )}

    {/* Side panel for recorder (web only) */}
    <RecorderSidePanel />
  </>
  );
}

export default Home;